import React, { useState, useRef, useEffect } from 'react';
import { t, Lang } from '../../../../i18n';
import { OPENAI_API_KEY } from '../../../../services/apiConfig';

interface InputBarProps {
  newMessage: string;
  setNewMessage: React.Dispatch<React.SetStateAction<string>>;
  handleSendMessage: (e: React.FormEvent) => void;
  lang: Lang;
  isSubmitted?: boolean;
  isCancelled?: boolean;
}

const InputBar: React.FC<InputBarProps> = ({ newMessage, setNewMessage, handleSendMessage, lang, isSubmitted = false, isCancelled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [supportsWebSpeechAPI, setSupportsWebSpeechAPI] = useState<boolean | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recognitionRef = useRef<any>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Check browser capabilities once on component mount
  useEffect(() => {
    const hasWebSpeech = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
    setSupportsWebSpeechAPI(hasWebSpeech);
  }, []);

  // Let CSS fully control the input height; no JS auto-resize.

  const handleMicClick = () => {
    try {
      if (isSubmitted || isProcessing) return;
      
      // Check if we're on mobile and force OpenAI Whisper
      const isMobile = window.innerWidth <= 768;
      
      if (isRecording) {
        // Stop recording
        if (isMobile || !supportsWebSpeechAPI) {
          stopMediaRecorder();
        } else {
          stopWebSpeech();
        }
      } else {
        // Start recording
        if (isMobile || !supportsWebSpeechAPI) {
          startMediaRecorder();
        } else {
          startWebSpeech();
        }
      }
    } catch (error) {
      setNewMessage('ERROR in microphone click handler: ' + error);
      setIsRecording(false);
      setIsProcessing(false);
    }
  };

  const startWebSpeech = () => {
    try {
      if (!supportsWebSpeechAPI) {
        setNewMessage('ERROR: Web Speech API not supported on this device');
        return;
      }
      
      const recognition = new (window.webkitSpeechRecognition || window.SpeechRecognition)();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = lang === 'nl' ? 'nl-NL' : 'en-US';
      
      // Add timeout fallback in case onresult never fires
      let resultReceived = false;
      let actualTranscript = '';
      const timeoutId = setTimeout(() => {
        if (!resultReceived) {
          setNewMessage('ERROR: Web Speech API timeout - no result received after 10 seconds');
          setIsRecording(false);
          if (recognitionRef.current) {
            recognitionRef.current.stop();
          }
        }
      }, 10000);
      
      recognition.onstart = () => {
        console.log('🎤 Web Speech API started');
        setNewMessage('🎤 STARTED - Web Speech API is running...');
        setIsRecording(true);
      };
      
      recognition.onresult = (event) => {
        try {
          resultReceived = true;
          clearTimeout(timeoutId);
          
          const transcript = event.results[0][0].transcript;
          actualTranscript = transcript; // Store the actual transcript
          console.log('🎤 Web Speech API result:', transcript);
          
          setNewMessage('🎤 RESULT FIRED - Transcript: "' + transcript + '"');
          if (!transcript || transcript.trim() === '') {
            setNewMessage('Web Speech API was used. Empty string was returned.');
          } else {
            setNewMessage(transcript);
          }
        } catch (error) {
          setNewMessage('ERROR in Web Speech API result handler: ' + error);
        }
        
        setIsRecording(false);
      };
      
      recognition.onerror = (event) => {
        try {
          resultReceived = true;
          clearTimeout(timeoutId);
          console.error('🎤 Web Speech API error:', event.error);
          setNewMessage('🎤 ERROR FIRED - ' + event.error);
        } catch (error) {
          setNewMessage('ERROR in Web Speech API error handler: ' + error);
        }
        setIsRecording(false);
      };
      
      recognition.onend = () => {
        try {
          resultReceived = true;
          clearTimeout(timeoutId);
          console.log('🎤 Web Speech API ended');
          setNewMessage('🎤 END FIRED - Final result: "' + actualTranscript + '"');
          
          // If we didn't get a result and didn't get an error, something went wrong
          if (!resultReceived) {
            setNewMessage('ERROR: Web Speech API ended without result or error');
          } else {
            setNewMessage('Web Speech API ended. Result was: "' + actualTranscript + '"');
          }
        } catch (error) {
          setNewMessage('ERROR in Web Speech API end handler: ' + error);
        }
        setIsRecording(false);
      };
      
      recognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      setNewMessage('ERROR starting Web Speech API: ' + error);
      setIsRecording(false);
    }
  };

  const stopWebSpeech = () => {
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    } catch (error) {
      setNewMessage('ERROR stopping Web Speech API: ' + error);
      setIsRecording(false);
    }
  };
  const startMediaRecorder = async () => {
    try {
      console.log('🎬 Starting MediaRecorder...');
      
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setNewMessage('ERROR: MediaRecorder not supported on this device');
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('🎤 Microphone access granted');
      
      // Try different audio formats for mobile compatibility
      let mediaRecorder: MediaRecorder = new MediaRecorder(stream); // Default initialization
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/wav'
      ];
      
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          console.log('✅ Using MIME type:', mimeType);
          mediaRecorder = new MediaRecorder(stream, { mimeType });
          break;
        }
      }
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        try {
          if (event.data.size > 0) {
            console.log('📦 Audio chunk received, size:', event.data.size);
            audioChunksRef.current.push(event.data);
          }
        } catch (error) {
          setNewMessage('ERROR in MediaRecorder data handler: ' + error);
        }
      };

      mediaRecorder.onstop = async () => {
        try {
          console.log('⏹️ MediaRecorder stopped, processing audio...');
          setIsRecording(false);
          setIsProcessing(true);
          
          stream.getTracks().forEach(track => track.stop());
          
          const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
          await transcribeAudio(audioBlob);
          
          setIsProcessing(false);
        } catch (error) {
          setNewMessage('ERROR in MediaRecorder stop handler: ' + error);
          setIsProcessing(false);
        }
      };

      mediaRecorder.onerror = (event) => {
        setNewMessage('ERROR in MediaRecorder: ' + (event as any).error || 'Unknown error');
        setIsRecording(false);
      };

      mediaRecorder.start();
      setIsRecording(true);
      console.log('🎙️ MediaRecorder started, recording...');
      
    } catch (error) {
      console.error('💥 Error starting MediaRecorder:', error);
      setNewMessage('ERROR starting MediaRecorder: ' + error);
      setIsRecording(false);
    }
  };

  const stopMediaRecorder = () => {
    try {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    } catch (error) {
      setNewMessage('ERROR stopping MediaRecorder: ' + error);
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    console.log('🔊 Starting OpenAI transcription...');
    console.log('📁 Audio blob size:', audioBlob.size, 'bytes');
    
    try {
      console.log('🤖 Sending to OpenAI Whisper...');
      
      const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENAI_API_KEY}`,
        },
        body: (() => {
          const formData = new FormData();
          formData.append('file', audioBlob, 'recording.webm');
          formData.append('model', 'whisper-1');
          return formData;
        })(),
      });
      
      if (!response.ok) {
        throw new Error(`OpenAI API error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ OpenAI transcription successful:', data.text);
      
      if (!data.text || data.text.trim() === '') {
        setNewMessage('OpenAI Whisper API was used. Empty string was returned.');
      } else {
        setNewMessage(data.text);
      }
      
    } catch (error) {
      console.error('❌ OpenAI transcription failed:', error);
      setNewMessage('OpenAI Whisper API failed. Using mock transcript.');
      setMockTranscript();
    }
  };

  const setMockTranscript = () => {
    if (lang === 'nl') {
      setNewMessage('Mock transcript (Dutch): Dit is een voorbeeld van spraakherkenning in het Nederlands.');
    } else {
      setNewMessage('Mock transcript (English): This is an example of speech recognition in English.');
    }
  };

  const handleAbort = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmitted || isCancelled) return;
    
    // Directly send "abort" message - set state and submit immediately
    // The form handler will process and clear the input
    setNewMessage('abort');
    
    // Submit immediately using requestAnimationFrame to ensure state is updated
    requestAnimationFrame(() => {
      if (formRef.current) {
        const syntheticEvent = {
          preventDefault: () => {},
          currentTarget: formRef.current,
          target: formRef.current,
        } as unknown as React.FormEvent;
        handleSendMessage(syntheticEvent);
      }
    });
  };

  return (
    <form ref={formRef} onSubmit={handleSendMessage} className="chat-input">
      <div className="input-container">
        <textarea
          ref={inputRef}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              formRef.current?.requestSubmit();
            }
          }}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          placeholder={isSubmitted ? `${t(lang, 'conversationSubmitted')} - ${t(lang, 'addCommentsOnly')}` : 
                      isCancelled ? t(lang, 'conversationCancelled') : 
                      t(lang, 'typeMessage')}
          className="message-input"
          rows={1}
          disabled={false} // Always enabled, even after submission
        />
        <button
          type="button"
          className={`mic-button ${isRecording ? 'recording' : ''} ${isProcessing ? 'processing' : ''}`}
          onClick={handleMicClick}
          disabled={isSubmitted || isProcessing}
          title={isRecording ? t(lang, 'recording') : t(lang, 'startVoiceInput')}
        >
          {isProcessing ? (
            <div className="loading-spinner"></div>
          ) : (
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" 
                fill="currentColor"
              />
              <path 
                d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" 
                fill="currentColor"
              />
            </svg>
          )}
        </button>
        
        <button
          type="button"
          className="abort-button"
          onClick={handleAbort}
          disabled={isSubmitted || isCancelled}
          title={t(lang, 'abort') || 'Abort'}
          aria-label={t(lang, 'abort') || 'Abort'}
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        
        <button type="submit" className="send-button" disabled={false} title={t(lang, 'send')} aria-label={t(lang, 'send')}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      
      {isSubmitted && (
        <div
          className="conversation-submitted-notice"
        >
          {t(lang, 'conversationSubmittedNotice')}
        </div>
      )}
      
      {isCancelled && (
        <div className="conversation-cancelled-notice">
          {t(lang, 'conversationCancelled')}
        </div>
      )}
    </form>
  );
};

export default InputBar; 